import { useEffect, useState, useRef } from 'react';
import moment from 'moment-timezone';
import { XCircle } from 'lucide-react';

export default function FloatingTimeCard({ onClose }) {
  const [localTime, setLocalTime] = useState(moment().format('YYYY-MM-DD • HH:mm:ss'));
  const [italyTime, setItalyTime] = useState(moment().tz('Europe/Rome').format('YYYY-MM-DD • HH:mm:ss'));
  const [utcTime, setUtcTime] = useState(moment.utc().format('YYYY-MM-DD • HH:mm:ss'));

  const cardRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // Set initial position (bottom-right)
  useEffect(() => {
    const cardWidth = 250; // same as width in style
    const cardHeight = 130; // approximate height of card
    setPosition({
      x: window.innerWidth - cardWidth - 50, // 50px margin from right
      y: window.innerHeight - cardHeight - 100, // 50px margin from bottom
    });
  }, []);

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => {
      setLocalTime(moment().format('YYYY-MM-DD • HH:mm:ss'));
      setItalyTime(moment().tz('Europe/Rome').format('YYYY-MM-DD • HH:mm:ss'));
      setUtcTime(moment.utc().format('YYYY-MM-DD • HH:mm:ss'));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Drag start
  const handleMouseDown = (e) => {
    setDragging(true);
    setOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  // Drag move
  const handleMouseMove = (e) => {
    if (dragging) {
      setPosition({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    }
  };

  // Drag end
  const handleMouseUp = () => {
    setDragging(false);
  };

  // Attach drag events
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  });

  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onMouseDown={handleMouseDown}
      style={{
        position: 'fixed',
        top: `${position.y}px`,
        left: `${position.x}px`,
        background: 'linear-gradient(145deg, #ffffff, #f3f3f3)',
        padding: '16px 20px',
        borderRadius: '16px',
        boxShadow: '0 6px 15px rgba(0,0,0,0.15)',
        zIndex: 9999,
        width: '250px',
        fontSize: '14px',
        color: '#333',
        fontFamily: 'Inter, Arial, sans-serif',
        cursor: dragging ? 'grabbing' : 'grab',
        userSelect: 'none',
        transition: dragging ? 'none' : 'top 0.2s, left 0.2s',
      }}
    >
      <div style={{ marginBottom: '10px' }}>
        <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', color: '#444' }}>
          <span style={{ fontWeight: 600, fontSize: '18px', color: '#1E3A5F' }}>UTC Time</span>
          <XCircle style={{ cursor: 'pointer' }} onClick={onClose} />
        </div>
        <div style={{ fontSize: '15px', fontWeight: 500, marginTop: '3px', color: '#444' }}>{utcTime}</div>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <span style={{ fontWeight: 600, fontSize: '18px', color: '#1E3A5F' }}>My Time</span>
        <div style={{ fontSize: '15px', fontWeight: 500, marginTop: '3px', color: '#444' }}>{localTime}</div>
      </div>

      <div>
        <span style={{ fontWeight: 600, fontSize: '18px', color: '#1E3A5F' }}>Italy Time</span>
        <div style={{ fontSize: '15px', fontWeight: 500, marginTop: '3px', color: '#444' }}>{italyTime}</div>
      </div>
    </div>
  );
}
